import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import './MultiSelect.css';

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'اختر...',
  searchPlaceholder = 'ابحث...',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [openUpward, setOpenUpward] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const goUp = spaceBelow < 250;
      setOpenUpward(goUp);
      setDropdownStyle(goUp ? {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 4,
        right: window.innerWidth - rect.right,
        width: rect.width,
        zIndex: 9999,
      } : {
        position: 'fixed',
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allValues = options.filter(o => o.value !== '').map(o => o.value);
  const allSelected = allValues.length > 0 && allValues.every(v => selected.includes(v));

  const toggleOption = (val) => {
    if (val === '') {
      // ضغطة ثانية على "الكل" تلغي كل الخيارات
      onChange(allSelected ? [] : allValues);
      return;
    }
    const next = selected.includes(val)
      ? selected.filter(v => v !== val)
      : [...selected, val];
    onChange(next);
  };

  const removeChip = (val, e) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== val));
  };

  const selectedLabels = selected.map(v => options.find(o => o.value === v)?.label).filter(Boolean);

  return (
    <div className="multi-select" ref={wrapperRef}>
      <div
        ref={triggerRef}
        className={`multi-select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
      >
        {selected.length === 0 ? (
          <span className="placeholder">{placeholder}</span>
        ) : (
          <div className="multi-select-chips">
            {selectedLabels.map((label, i) => (
              <span key={selected[i]} className="multi-select-chip">
                {label}
                <X size={12} onClick={(e) => removeChip(selected[i], e)} />
              </span>
            ))}
          </div>
        )}
        <ChevronDown size={18} className={`multi-select-arrow ${isOpen ? 'rotated' : ''}`} />
      </div>

      {isOpen && !disabled && (
        <div className={`multi-select-dropdown ${openUpward ? 'opens-upward' : ''}`} style={dropdownStyle}>
          <div className="multi-select-search">
            <Search size={16} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="multi-select-options">
            <div
              className={`multi-select-option ${allSelected ? 'selected' : ''}`}
              onClick={() => toggleOption('')}
            >
              <span className="multi-select-checkbox">{allSelected ? '✓' : ''}</span>
              الكل
            </div>
            {filteredOptions.filter(o => o.value !== '').map(option => (
              <div
                key={option.value}
                className={`multi-select-option ${selected.includes(option.value) ? 'selected' : ''}`}
                onClick={() => toggleOption(option.value)}
              >
                <span className="multi-select-checkbox">{selected.includes(option.value) ? '✓' : ''}</span>
                {option.label}
              </div>
            ))}
            {filteredOptions.filter(o => o.value !== '').length === 0 && (
              <div className="multi-select-empty">لا توجد نتائج</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
